/**
 * The work that follows a successful task create, before the modal may close.
 *
 * It used to run strictly in series inside the modal, with the cache
 * invalidations awaited too:
 *
 *   create -> order -> labels -> await invalidate(tasks) -> await invalidate(...)
 *
 * `['tasks', spaceId]` is an active query while the modal is open, so awaiting
 * its invalidation meant blocking the close on a full refetch of the space's
 * task list. That put four to six sequential round trips between clicking
 * Create and the modal closing (#3177).
 *
 * Two changes, and no optimistic close:
 *
 *  - the two follow-up writes are independent of each other, so they run
 *    concurrently and are still awaited. The task is therefore complete and
 *    correctly positioned the moment it appears, with no row jumping into place
 *    or labels popping in afterwards, and nothing to roll back on failure.
 *  - the cache refresh is fired and not awaited. The create response and the
 *    taskAdded socket event already put the task on screen, so the refetch is a
 *    background reconciliation rather than something the user waits on.
 *
 * A failed write still rejects, so the caller keeps its error handling.
 */
export interface SettleCreatedTaskArgs {
  /** Places the new task in the space ordering. */
  insertIntoOrder: () => Promise<unknown>;
  /** Attaches the chosen labels, or null when none were picked. */
  attachLabels: (() => Promise<unknown>) | null;
  /** Fires the cache invalidations. Deliberately synchronous and not awaited. */
  refreshCaches: () => void;
}

export async function settleCreatedTask({
  insertIntoOrder,
  attachLabels,
  refreshCaches,
}: SettleCreatedTaskArgs): Promise<void> {
  const writes: Array<Promise<unknown>> = [insertIntoOrder()];
  if (attachLabels) {
    writes.push(attachLabels());
  }

  await Promise.all(writes);

  refreshCaches();
}
